/**
 * Centralized Authentication Utilities
 * 
 * This module provides consistent session management across the platform.
 * All logout, session clearing, and security-sensitive auth operations
 * should use these utilities to ensure:
 * 
 * 1. Global session revocation (server-side)
 * 2. Complete localStorage cleanup (client-side)
 * 3. Consistent redirect behavior
 * 
 * Security Model:
 * - Access tokens are short-lived (configured in Supabase Dashboard)
 * - Refresh tokens are stored in localStorage (SPA constraint)
 * - All logouts trigger global session invalidation
 * - Password changes invalidate all sessions
 */

import { supabase } from '@/integrations/supabase/client';
import { signOutThroughAuthLibrary } from '@/lib/authLibrarySignOut';
import { clearMarketingAuthHintCookie, setMarketingAuthHintCookie } from '@/lib/marketingAuthHint';
import { isMfaRequiredForEmail, isPreviewMfaBypass as isPreviewMfaBypassRaw } from '@/shared/mfaBypassAllowlist';

/**
 * Storage keys that should be cleared on logout
 */
const AUTH_STORAGE_KEYS = [
  'currentOrgId',
  'currentOrgUserId',
  'currentViewMode',
  'isMspContext',
  'mspHomeOrgId',
  'recoveryMode', // Password recovery session restriction flag
] as const;

/**
 * Recovery mode key - when set, user is in a password recovery session
 * and should be restricted from accessing protected routes until
 * password reset is completed.
 */
const RECOVERY_MODE_KEY = 'recoveryMode';
const SESSION_LOCK_STORAGE_KEY = 'sessionLockState';
const PENDING_SIGNUP_VERIFICATION_STORAGE_KEY = 'pendingSignupVerificationState';
const ORGANIZATION_CONTEXT_STORAGE_KEYS = ['currentOrgId', 'currentOrgUserId', 'isMspContext', 'mspHomeOrgId'] as const;

interface SessionLockState {
  userId: string;
  userEmail: string;
  lockedAt: string;
}

interface PendingSignupVerificationState {
  email: string;
  createdAt: string;
  authPath?: string;
}

/**
 * ENT-3461 (D4): the last method a returning visitor used to sign in, kept in
 * localStorage as a pure convenience so the sign-in page can offer a one-click
 * "Continue as you@company.com". It stores NO secrets and NO tokens — only the
 * method and the email — so it is safe to persist across a closed tab / expired
 * session. It is cleared ONLY by an explicit "Use a different account" /
 * "Not you?" (ENT-4200 D4 amendment: a standard `signOutGlobally` no longer
 * clears it, or the returning-visitor convenience would never fire). `method`
 * distinguishes an SSO domain (re-probe + redirect on return) from a password
 * account (prefill the email, ask only for the password).
 */
const LAST_AUTH_METHOD_STORAGE_KEY = 'lastAuthMethod';

export type LastAuthMethod = {
  method: 'sso' | 'password';
  email: string;
};

export function readLastAuthMethod(): LastAuthMethod | null {
  try {
    const rawValue = localStorage.getItem(LAST_AUTH_METHOD_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue) as Partial<LastAuthMethod>;
    if (
      (parsed.method !== 'sso' && parsed.method !== 'password') ||
      typeof parsed.email !== 'string' ||
      parsed.email.trim().length === 0
    ) {
      localStorage.removeItem(LAST_AUTH_METHOD_STORAGE_KEY);
      return null;
    }

    return { method: parsed.method, email: parsed.email };
  } catch {
    try {
      localStorage.removeItem(LAST_AUTH_METHOD_STORAGE_KEY);
    } catch {
      // Ignore cleanup failures.
    }
    return null;
  }
}

export function persistLastAuthMethod(entry: LastAuthMethod): void {
  const normalizedEmail = entry.email.trim().toLowerCase();
  if (!normalizedEmail || (entry.method !== 'sso' && entry.method !== 'password')) {
    return;
  }

  try {
    localStorage.setItem(
      LAST_AUTH_METHOD_STORAGE_KEY,
      JSON.stringify({ method: entry.method, email: normalizedEmail } satisfies LastAuthMethod),
    );
  } catch (error) {
    console.error('Failed to persist last auth method:', error);
  }
}

export function clearLastAuthMethod(): void {
  try {
    localStorage.removeItem(LAST_AUTH_METHOD_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear last auth method:', error);
  }
}

/**
 * Checks if the current session is in password recovery mode.
 * When in recovery mode, the user should only be able to reset their password.
 */
export function isInRecoveryMode(): boolean {
  try {
    return localStorage.getItem(RECOVERY_MODE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Sets the recovery mode flag.
 * Call this when a PASSWORD_RECOVERY event is detected.
 */
export function setRecoveryMode(enabled: boolean): void {
  try {
    if (enabled) {
      localStorage.setItem(RECOVERY_MODE_KEY, 'true');
      console.log('[Security] Recovery mode ENABLED - user restricted to password reset only');
    } else {
      localStorage.removeItem(RECOVERY_MODE_KEY);
      console.log('[Security] Recovery mode CLEARED - normal access restored');
    }
  } catch (error) {
    console.error('Failed to set recovery mode:', error);
  }
}

/**
 * Clears recovery mode. Call after successful password update.
 */
export function clearRecoveryMode(): void {
  setRecoveryMode(false);
}

export function isRecoveryPath(pathname: string): boolean {
  // ENT-4311: `/auth/reset` is the scanner-safe recovery landing route, so it is an
  // allowed location during recovery mode (never blocked as a "protected route").
  return pathname === '/' || pathname === '/auth' || pathname === '/auth/reset';
}

export function shouldBlockProtectedRouteDuringRecovery(pathname: string): boolean {
  return !isRecoveryPath(pathname) && isInRecoveryMode();
}

export function reconcileRecoveryStateAfterStandardAuth(): void {
  clearRecoveryMode();
  clearPendingSignupVerification();
}

// ENT-1418 opt-out: testers can force MFA back on in preview with
// `?mfa=on`. The first read of the URL param persists to sessionStorage so
// the flag survives redirects (e.g. password sign-in → OAuth bounce → app
// shell). `?mfa=off` clears the override and falls back to the default
// preview bypass.
const PREVIEW_MFA_OVERRIDE_STORAGE_KEY = 'preview-mfa-override';

function readPreviewMfaOverride(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const params = new URLSearchParams(window.location.search ?? '');
    const fromUrl = params.get('mfa');
    if (fromUrl === 'on') {
      try { window.sessionStorage?.setItem(PREVIEW_MFA_OVERRIDE_STORAGE_KEY, 'on'); } catch { /* noop */ }
      return 'on';
    }
    if (fromUrl === 'off') {
      try { window.sessionStorage?.removeItem(PREVIEW_MFA_OVERRIDE_STORAGE_KEY); } catch { /* noop */ }
      return undefined;
    }
    return window.sessionStorage?.getItem(PREVIEW_MFA_OVERRIDE_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function isMfaRequired(userEmail?: string | null): boolean {
  return isMfaRequiredForEmail(
    userEmail,
    import.meta.env.VITE_DISABLE_MFA_ALLOWLIST,
    import.meta.env.VITE_SENTRY_ENVIRONMENT,
    typeof window !== 'undefined' ? window.location?.hostname : undefined,
    readPreviewMfaOverride(),
  );
}

// ENT-1418: preview-only MFA bypass for every user. See `isPreviewMfaBypass`
// in `@/shared/mfaBypassAllowlist` for the policy contract. Staging and
// production are unaffected.
export function isPreviewMfaBypass(): boolean {
  return isPreviewMfaBypassRaw(
    import.meta.env.VITE_SENTRY_ENVIRONMENT,
    typeof window !== 'undefined' ? window.location?.hostname : undefined,
    readPreviewMfaOverride(),
  );
}

export function getSessionLockState(): SessionLockState | null {
  try {
    const rawValue = sessionStorage.getItem(SESSION_LOCK_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue) as Partial<SessionLockState>;
    if (
      typeof parsed.userId !== 'string' ||
      typeof parsed.userEmail !== 'string' ||
      typeof parsed.lockedAt !== 'string'
    ) {
      sessionStorage.removeItem(SESSION_LOCK_STORAGE_KEY);
      return null;
    }

    return parsed as SessionLockState;
  } catch {
    try {
      sessionStorage.removeItem(SESSION_LOCK_STORAGE_KEY);
    } catch {
      // Ignore sessionStorage cleanup failures.
    }
    return null;
  }
}

export function setSessionLockState({
  userId,
  userEmail,
}: {
  userId: string;
  userEmail: string;
}): void {
  try {
    sessionStorage.setItem(
      SESSION_LOCK_STORAGE_KEY,
      JSON.stringify({
        userId,
        userEmail,
        lockedAt: new Date().toISOString(),
      } satisfies SessionLockState),
    );
  } catch (error) {
    console.error('Failed to persist session lock state:', error);
  }
}

export function clearSessionLockState(): void {
  try {
    sessionStorage.removeItem(SESSION_LOCK_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear session lock state:', error);
  }
}

export function isUserEmailVerified(user?: { email_confirmed_at?: string | null } | null): boolean {
  return Boolean(user?.email_confirmed_at);
}

export function readPendingSignupVerification(authPath?: string): PendingSignupVerificationState | null {
  try {
    const rawValue = sessionStorage.getItem(PENDING_SIGNUP_VERIFICATION_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue) as Partial<PendingSignupVerificationState>;
    if (typeof parsed.email !== 'string' || typeof parsed.createdAt !== 'string') {
      sessionStorage.removeItem(PENDING_SIGNUP_VERIFICATION_STORAGE_KEY);
      return null;
    }

    const state: PendingSignupVerificationState = {
      email: parsed.email,
      createdAt: parsed.createdAt,
      authPath: typeof parsed.authPath === 'string' ? parsed.authPath : undefined,
    };

    if (authPath) {
      const normalizedAuthPath = authPath.trim();
      if (normalizedAuthPath && state.authPath !== normalizedAuthPath) {
        try {
          if (!state.authPath) {
            sessionStorage.removeItem(PENDING_SIGNUP_VERIFICATION_STORAGE_KEY);
          }
        } catch {
          // Ignore cleanup failures.
        }
        return null;
      }
    }

    return state;
  } catch {
    try {
      sessionStorage.removeItem(PENDING_SIGNUP_VERIFICATION_STORAGE_KEY);
    } catch {
      // Ignore cleanup failures.
    }
    return null;
  }
}

export function persistPendingSignupVerification(email: string, authPath?: string): void {
  const normalizedEmail = email.trim().toLowerCase();
  const normalizedAuthPath = authPath?.trim() || undefined;
  if (!normalizedEmail) {
    return;
  }

  try {
    sessionStorage.setItem(
      PENDING_SIGNUP_VERIFICATION_STORAGE_KEY,
      JSON.stringify({
        email: normalizedEmail,
        createdAt: new Date().toISOString(),
        authPath: normalizedAuthPath,
      } satisfies PendingSignupVerificationState),
    );
  } catch (error) {
    console.error('Failed to persist pending signup verification state:', error);
  }
}

export function clearPendingSignupVerification(authPath?: string): void {
  try {
    if (!authPath) {
      sessionStorage.removeItem(PENDING_SIGNUP_VERIFICATION_STORAGE_KEY);
      return;
    }

    const pendingState = readPendingSignupVerification();
    if (!pendingState || pendingState.authPath === authPath) {
      sessionStorage.removeItem(PENDING_SIGNUP_VERIFICATION_STORAGE_KEY);
    }
  } catch (error) {
    console.error('Failed to clear pending signup verification state:', error);
  }
}

/**
 * Clears the APP's own auth-scoped data from localStorage:
 * - Organization context
 * - View mode preferences
 * - Agent chat history (contains org-scoped data)
 *
 * ENT-5492 (D2): it never touches the auth library's persisted session
 * (`sb-*-auth-token`). Sign the session out through
 * `supabase.auth.signOut(...)` first — so the library, and every other tab,
 * learn of it — and call this afterwards, never instead of it.
 */
export function clearLocalAuthState(options?: {
  preserveKeys?: readonly string[];
}): void {
  try {
    const preserveKeys = new Set(options?.preserveKeys ?? []);

    clearMarketingAuthHintCookie();
    clearSessionLockState();

    // Clear dynamically named chat entries. Iterate through the Storage
    // interface so this works with browser storage and the test fallback alike.
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (!key) {
        continue;
      }

      // Clear agent chat history (org-scoped)
      if (key.startsWith('agent_chat_history:')) {
        localStorage.removeItem(key);
      }
    }
    
    // Clear known auth-related keys
    for (const key of AUTH_STORAGE_KEYS) {
      if (preserveKeys.has(key)) {
        continue;
      }
      localStorage.removeItem(key);
    }
  } catch (error) {
    // localStorage may be unavailable in some contexts
    console.error('Failed to clear local auth state:', error);
  }
}

// ENT-4349 / ENT-5492 D2: the lock-free removal lives with the fail-closed
// library sign-out it backs (src/lib/authLibrarySignOut.ts).
export { removeStoredAuthSessionWithoutLock } from '@/lib/authLibrarySignOut';

/**
 * Performs a complete sign-out with global session revocation.
 * 
 * This function:
 * 1. Revokes ALL sessions server-side (prevents stolen token reuse)
 * 2. Falls back to local sign-out if global fails
 * 3. Clears all client-side auth state
 * 4. Redirects to the specified path
 * 
 * @param redirectPath - Path to redirect after logout (default: '/auth')
 * @param useReplace - Use location.replace instead of href (default: true)
 */
export async function signOutGlobally(
  redirectPath: string = '/auth',
  useReplace: boolean = true
): Promise<void> {
  // CRITICAL: Global scope revokes ALL sessions server-side
  const globalOutcome = await signOutThroughAuthLibrary('global');
  if (globalOutcome === 'superseded' || globalOutcome === 'aborted') return;

  // ENT-4200 (D4 amendment): a standard sign-out no longer forgets the
  // remembered sign-in method. Every sign-out button (sidebar/admin/vendor/
  // session-manager) routes through here, so clearing it here meant "nothing was
  // ever remembered" — the returning-visitor "Continue as {email}" convenience
  // never fired. The method is a pure, secret-free convenience, so it survives a
  // sign-out; it is forgotten ONLY by an explicit "Use a different account" /
  // "Not you?" (forgetRememberedAccount → clearLastAuthMethod) and by
  // readLastAuthMethod's self-heal of a malformed value.
  // Fallback: ensure local session is cleared even if global fails
  const localOutcome = await signOutThroughAuthLibrary('local');
  if (localOutcome === 'superseded' || localOutcome === 'aborted') return;

  // Always clear client-side state
  clearLocalAuthState();

  // Navigate away from authenticated routes
  if (useReplace) {
    window.location.replace(redirectPath);
  } else {
    window.location.href = redirectPath;
  }
}

/**
 * Performs a local-only sign-out (current device only).
 * 
 * WARNING: This should only be used for specific use cases like
 * switching accounts. For security-sensitive operations (logout button,
 * password change), always use signOutGlobally().
 * 
 * @param redirectPath - Path to redirect after logout
 */
export async function signOutLocally(
  redirectPath: string = '/auth',
  options?: {
    preserveOrganizationContext?: boolean;
  },
): Promise<void> {
  const outcome = await signOutThroughAuthLibrary('local');
  if (outcome === 'superseded' || outcome === 'aborted') return;

  clearLocalAuthState({
    preserveKeys: options?.preserveOrganizationContext ? ORGANIZATION_CONTEXT_STORAGE_KEYS : undefined,
  });
  window.location.replace(redirectPath);
}


/**
 * ENT-4295: revoke sessions after a password reset via a RAW, lock-independent,
 * abortable request to GoTrue's logout endpoint. Deliberately NOT
 * `supabase.auth.signOut()` — that takes the shared supabase-js auth-client lock,
 * so a stalled logout could wedge post-reset routing or the destination layouts'
 * auth calls. Bounded by an AbortController and AWAITED so callers can FAIL
 * CLOSED. Returns true ONLY when GoTrue confirms revocation (HTTP 2xx); returns
 * false on any non-2xx, network error, or timeout/abort. Never throws.
 *
 * `scope: 'others'` keeps the current (just-verified) session and revokes every
 * other device/session; `scope: 'global'` revokes every session including this
 * one (used by the fail-closed fallback to confirm server-side teardown before
 * redirecting).
 */
export async function revokeSessionsAfterPasswordReset(
  scope: 'others' | 'global',
  accessToken: string,
  timeoutMs: number,
): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/auth/v1/logout?scope=${scope}`,
      {
        method: 'POST',
        headers: {
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? '',
          Authorization: `Bearer ${accessToken}`,
        },
        signal: controller.signal,
      },
    );
    if (!response.ok) {
      console.error(`Session revocation (scope=${scope}) after password reset returned HTTP ${response.status}`);
      return false;
    }
    return true;
  } catch (error) {
    console.error(`Session revocation (scope=${scope}) after password reset failed:`, error);
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Handles session invalidation after password changes.
 * 
 * This should be called after a successful password update to ensure:
 * 1. All existing sessions are invalidated
 * 2. User must re-authenticate with new credentials
 * 
 * @param showMessage - Callback to display a message to the user
 */
export async function invalidateSessionsAfterPasswordChange(
  showMessage?: (message: string) => void
): Promise<void> {
  showMessage?.('Password updated. Signing out all sessions for security...');
  
  await signOutGlobally('/auth', true);
}

/**
 * Checks if the current session is valid.
 * 
 * This can be used for session validation on app load or before
 * sensitive operations.
 */
export async function validateSession(): Promise<{
  valid: boolean;
  userId: string | null;
  error?: string;
}> {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      clearMarketingAuthHintCookie();
      return { valid: false, userId: null, error: error.message };
    }
    
    if (!session) {
      clearMarketingAuthHintCookie();
      return { valid: false, userId: null };
    }
    
    // Check if token is expired
    const expiresAt = session.expires_at;
    if (expiresAt && expiresAt * 1000 < Date.now()) {
      clearMarketingAuthHintCookie();
      return { valid: false, userId: null, error: 'Session expired' };
    }

    setMarketingAuthHintCookie();
    return { valid: true, userId: session.user.id };
  } catch (error) {
    clearMarketingAuthHintCookie();
    return { 
      valid: false, 
      userId: null, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

type MfaStatus = {
  enrolled: boolean;
  verified: boolean;
  factorId: string | null;
  /**
   * ENT-1707: `true` when the MFA probe could NOT be completed (transient
   * gotrue-js navigator-lock contention / aborted request under parallel
   * load), as distinct from a definitive "this user has no MFA enrolled".
   * Callers MUST NOT treat `probeError` as "no MFA" — doing so is what made
   * the StepUp/full-scan flow refuse to advance to the TOTP step and show a
   * misleading "enable MFA" toast (ENT-1568). On `probeError` the caller
   * should surface a transient "try again" state, never the no-MFA path.
   */
  probeError?: boolean;
};

const MFA_STATUS_RETRY_DELAYS_MS = [0, 250, 750] as const;

async function fetchMfaStatusOnce(): Promise<MfaStatus> {
  const { data: mfaData, error: mfaError } =
    await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (mfaError) {
    throw mfaError;
  }
  const { data: factorsData, error: factorsError } =
    await supabase.auth.mfa.listFactors();
  if (factorsError) {
    throw factorsError;
  }

  const verifiedFactor = factorsData?.totp?.find((f) => f.status === 'verified');

  return {
    enrolled: (factorsData?.totp?.length ?? 0) > 0,
    verified: mfaData?.currentLevel === 'aal2',
    factorId: verifiedFactor?.id ?? null,
  };
}

let inFlightMfaStatus: Promise<MfaStatus> | null = null;

/**
 * Security utility to check MFA enrollment status.
 *
 * ENT-1568 / ENT-1707: the two underlying Supabase MFA calls
 * (`getAuthenticatorAssuranceLevel` + `listFactors`) are serialized behind
 * gotrue-js's navigator `LockManager`. Under parallel load they transiently
 * throw lock-contention / aborted-request errors ("AbortError: Lock broken
 * by another request"). The original ENT-1568 fix added a fixed-backoff
 * retry, but a retry only masks the contention — it does not remove it — and
 * the flake recurred on `staging.yml` (ENT-1707).
 *
 * The durable fix has two parts:
 *  1. **Request coalescing** — the three independent callers
 *     (`RecentMfaActionDialog`, `ElevateSuperAdminDialog`,
 *     `BreakGlassRequestDialog`) plus any background session refresh now
 *     share a SINGLE in-flight probe instead of each acquiring the gotrue
 *     lock concurrently. Removing the self-contention is the primary fix;
 *     the bounded retry below is now only secondary defense-in-depth.
 *  2. **Honest failure** — when every attempt fails we return
 *     `probeError: true` (still `enrolled: false` for back-compat) instead
 *     of silently reporting a definitive "no MFA". Callers branch on
 *     `probeError` to show a transient "try again" state rather than the
 *     misleading no-MFA path that refused to render the TOTP input on
 *     `e2e/supplierlens-ui-exits.spec.ts:248`.
 */
export function checkMFAStatus(): Promise<MfaStatus> {
  if (inFlightMfaStatus) {
    return inFlightMfaStatus;
  }
  const probe = runMfaStatusProbe().finally(() => {
    inFlightMfaStatus = null;
  });
  inFlightMfaStatus = probe;
  return probe;
}

async function runMfaStatusProbe(): Promise<MfaStatus> {
  let lastError: unknown = null;
  for (const delayMs of MFA_STATUS_RETRY_DELAYS_MS) {
    if (delayMs > 0) {
      await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
    }
    try {
      return await fetchMfaStatusOnce();
    } catch (error) {
      lastError = error;
      // Continue to next retry; keep last error for the final log line.
    }
  }
  console.error('Error checking MFA status:', lastError);
  // ENT-1707: surface probeError instead of a false-negative "not enrolled".
  return { enrolled: false, verified: false, factorId: null, probeError: true };
}

export async function isUserSuperAdmin(userId: string | null | undefined): Promise<boolean> {
  if (!userId) {
    return false;
  }

  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .eq('role', 'super_admin')
      .maybeSingle();

    if (error) {
      console.error('Error checking super admin role:', error);
      return false;
    }

    return Boolean(data);
  } catch (error) {
    console.error('Error checking super admin role:', error);
    return false;
  }
}
